/**
 * `useEncounter(compendium, encounterId)` — the React surface for the
 * server-authoritative encounter room (Lane U / U1), the encounter analogue of
 * {@link useRemoteRoster}. It opens a **real browser `WebSocket`** to the
 * server's encounter room at `WS_ENCOUNTER_PATH`, drives a
 * {@link RemoteEncounterController} (optimistic local apply + last-write-wins
 * reconcile to `encounter_change`), and adapts it into React.
 *
 * It is a **thin wrapper over {@link useRoomController}** — the generic owns the
 * whole controller↔React bridge; this wrapper only supplies the encounter
 * connection factory (which fires the per-room join on socket-open). It returns
 * `{ loading, state, dispatch, undo, redo, undoState, connStatus, actorId }` — the
 * same field names the roster hook surfaces. There is no `computed` (encounter
 * state is plain tracked values; nothing is recomputed from a compendium like the
 * roster's mech stats); the `compendium` is accepted because it gates re-connection
 * and mirrors `useRemoteRoster`'s signature.
 *
 * **The handshake differs from the roster's.** The roster room is joined
 * implicitly on socket open; the encounter room is per-encounter and resolved
 * from the client's **first message**. So {@link openEncounterConnection} fires
 * `controller.sendJoin()` on the socket's `open` event — the controller is not
 * `connected` until the server replies `encounter_welcome`. `encounterId` is a
 * required hook parameter for exactly this reason.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { Cell, EncounterCommand, EncounterUndoState } from "@lancer-vtt/domain";
import { WS_ENCOUNTER_PATH, err, type EncounterIntent, type PromptRequest, type WireMeasureEnd } from "@lancer-vtt/shared";
import {
  RemoteEncounterController,
  type EncounterConnection,
  type EncounterDispatchResult,
  type EncounterSnapshot,
  type BroadcastResolution,
  type EncounterRejection,
  type PendingMove,
  type PingEvent,
  type SequenceState,
  type CursorEvent,
  type MovePreviewEvent,
  type MeasureEvent,
} from "./remoteEncounterController";
import type { AutoActivationNotice } from "./autoActivationNotice";
import { LINK_DOWN_NOTE, roomWsUrl, startKeepalive, type ConnStatus } from "../roomWs";
import { useRoomController } from "../useRoomController";

/**
 * Connection lifecycle the page surfaces — the unified command-room {@link ConnStatus}.
 * Kept as a re-exported alias so existing `EncounterConnStatus` imports are unchanged.
 */
export type EncounterConnStatus = ConnStatus;

/** Resolve the encounter room ws URL from the browser location (mirrors `rosterWsUrl`). */
export function encounterWsUrl(): string {
  return roomWsUrl(WS_ENCOUNTER_PATH);
}

/**
 * The default real-`WebSocket` wiring — opens a browser `WebSocket` to `url`,
 * wraps it as an {@link EncounterConnection}, constructs a
 * {@link RemoteEncounterController} for `encounterId`, pipes inbound messages into
 * it, and **sends the explicit `encounter_join` on open** (the per-room handshake;
 * there is no join-on-open on the server). Extracted so the browser code path is
 * round-trip-tested against a real backend — it is the exact code the hook runs.
 *
 * `onStatus` is bridged from the socket lifecycle; the controller flips to
 * `connected` on `encounter_welcome` (the hook bridges that separately).
 */
export function openEncounterConnection(
  encounterId: string,
  target: { url: string; token: string },
  onStatus: (s: EncounterConnStatus) => void,
): { controller: RemoteEncounterController; close: () => void } {
  const ws = new WebSocket(target.url);
  // The connection carries both transport AND the identity token (Decision 0016).
  const conn: EncounterConnection = {
    send: (data) => ws.send(data),
    close: () => ws.close(),
    token: target.token,
  };
  const c = new RemoteEncounterController(encounterId, conn);
  ws.addEventListener("message", (ev) => c.onMessage(String(ev.data)));
  // Keep the path warm — an idle socket is silently evicted mid-route (see `startKeepalive`).
  let stopKeepalive: (() => void) | null = null;
  ws.addEventListener("open", () => {
    onStatus("connecting");
    // The per-room handshake: name the room. Nothing the client sends before this
    // is acted on, and the controller is not `connected` until the welcome lands.
    c.sendJoin();
    // A dead path is a drop now, not when TCP finally gives up — see `startKeepalive`.
    stopKeepalive = startKeepalive(ws, { onDead: () => onStatus("disconnected") });
  });
  ws.addEventListener("close", () => {
    stopKeepalive?.();
    onStatus("disconnected");
  });
  ws.addEventListener("error", () => onStatus("disconnected"));
  return {
    controller: c,
    close: () => {
      stopKeepalive?.();
      ws.close();
    },
  };
}

/** What `useEncounter` returns — the roster hook's corresponding fields plus `connStatus`. */
export type UseEncounter = {
  /** The viewer's slot is the GM's; the pre-activation preview reads it ({@link UseEncounterOptions.isGm}). */
  isGm: boolean;
  /** True until the welcome handshake seeds authoritative state. */
  loading: boolean;
  /**
   * The reconciled authoritative encounter state, or null before the welcome. Declared as the
   * bare `EncounterState` (unchanged) — the same pragmatic simplification the fog-of-war
   * concealment axis already relies on (a concealed combatant is silently absent from this map
   * with no type-level modeling either): most of `state` is read here for domain/rules
   * affordance mirrors (range, LoS, movement, targeting legality) that only ever touch
   * preserved fields (`pos`/`base.size`/`controllerSlotId`/…) and must keep taking the real
   * `@lancer-vtt/domain`/`@lancer-vtt/rules` `Combatant`/`EncounterState` types unchanged.
   * The type is widened ONLY at the specific render
   * leaves that display ONE combatant's own stat block for possibly-any viewer (the per-unit
   * inspector, the token's HP bar) — see `ProjectedCombatant`/`ProjectedEncounterState`
   * (`@lancer-vtt/domain`) and their call sites in `CombatApp.tsx`/`tokenChrome.ts`.
   */
  state: EncounterSnapshot["state"] | null;
  /** Optimistically apply + send a command (LWW-reconciled to the server's broadcast). */
  dispatch: (command: EncounterCommand) => EncounterDispatchResult;
  /**
   * Optimistically apply + send several commands as **one user gesture** — the pre-activation
   * Boost's `activate_unit` + `boost` pair (`projects/pre-activation-affordances.md` D6/3.1),
   * built by `withLeadingActivation`. Two ORDINARY commands, deliberately never a `batch` (a batch
   * skips both hook chains at the room); rejections inside the span are coalesced so a lost race
   * reports its real cause. See {@link RemoteController.dispatchGesture}.
   */
  dispatchGesture: (commands: EncounterCommand[]) => EncounterDispatchResult;
  /**
   * Dispatch a PLAY-mode voluntary move:
   * optimistically applies the `move_token` shape locally, but sends the `request_move`
   * **intent** on the wire — the checkpoint pre-apply seam's entry point.
   */
  dispatchMove: (combatantId: string, to: Cell, opts?: { actorId?: string; path?: Cell[]; choices?: Record<string, string> }) => EncounterDispatchResult;
  /** Ask the server to undo this actor's last command. */
  undo: () => boolean;
  /** Ask the server to redo this actor's last undone command. */
  redo: () => boolean;
  /** This actor's server-reported undo/redo availability (reliable — one long-lived conn). */
  undoState: EncounterUndoState;
  /** Live ws connection status (connecting → connected once welcomed → disconnected). */
  connStatus: EncounterConnStatus;
  /**
   * The reconnect budget is spent and nothing will retry — a remount or a page reload is the
   * only recovery. Implies `connStatus === "disconnected"`, but not the reverse: an ordinary
   * still-retrying drop is `disconnected` with `linkLost: false`. Matters here because the
   * board stays mounted through a drop, so a dead room renders a complete but permanently
   * stale fight. See `useRoomController`'s `linkLost` doc for the full contract.
   */
  linkLost: boolean;
  /** Reconnect NOW — drop the pending backoff and re-open immediately. The automatic
   *  retry already recovers on its own; this exists so a GM staring at a stale board mid-fight can
   *  act instead of wait. See `useRoomController`'s `reconnect` doc. */
  reconnect: () => void;
  /** Our server-assigned actor id, or null until `encounter_welcome` arrives. */
  actorId: string | null;
  /** Send a resolution intent (attack/activation) — the authority resolves it. */
  sendIntent: (intent: EncounterIntent) => boolean;
  /** The pending prompt awaiting this client's answer, or null. */
  prompt: PromptRequest | null;
  /** Answer the pending prompt with an option id (re-submits the intent with the choice). */
  answerPrompt: (optionId: string) => boolean;
  /**
   * Answer a SPECIFIC held prompt by id, never the head — the take flow's seam: it captures a
   * checkpoint prompt's id into a local at offer time
   * and must re-submit that exact prompt's answer later, even if a nested sibling prompt has since
   * become {@link prompt} (the head). A no-op (returns false) if we no longer hold a prompt with
   * this id.
   */
  answerPromptById: (promptId: string, optionId: string) => boolean;
  /**
   * Answer a COMMS decision card by its prompt id; a GM falls back to the server's record when
   * this client no longer holds the prompt. `"gone"` means nothing here can answer it. See
   * `RemoteEncounterController.answerDecision`.
   */
  answerDecision: (promptId: string, optionId: string) => "sent" | "gone" | "offline";
  /** Whether this client still holds a prompt with this id (not only the head). */
  hasPrompt: (promptId: string) => boolean;
  /**
   * Answer a pending **direction** prompt with a board cell —
   * re-submits the intent with `reactionToward` filled, the spatial twin of {@link answerPrompt}.
   */
  answerPromptDirection: (cell: Cell) => boolean;
  /** Dismiss the pending prompt without answering. */
  dismissPrompt: () => void;
  /** The mover whose just-dispatched move is predicted to halt — drives the transient "awaiting a reaction" banner, or null. A render-time snapshot; see {@link isMoveHeld} for the non-stale presentation gate. */
  pendingMove: PendingMove | null;
  /**
   * Was `combatantId`'s just-dispatched move predicted to halt (the presentation-commit
   * gate)? Reads the controller's mutable field FRESH at call time (not a captured render-time
   * value) — `CombatApp.tsx`'s `onLocalMove` wrapper calls this in the SAME synchronous tick
   * `finishTokenDrag` calls `dispatchMove` then `onLocalMove` in, so a `pendingMove` snapshot
   * captured at the START of that render (necessarily still `null`, since `dispatchMove` hasn't
   * run yet) would never gate anything — this must re-read the controller, not close over a value.
   */
  isMoveHeld: (combatantId: string) => boolean;
  /** The mover whose declared move is currently table-wide paused (the server's broadcast — every
   *  connected client sees this, not just the mover/decider), or null. */
  tablePausedMoverId: string | null;
  /** The session resolution feed (oldest → newest) — the result-card source. */
  resolutions: BroadcastResolution[];
  /** The most recent broadcast resolution (transient card), or null. */
  latestResolution: BroadcastResolution | null;
  /** Drop a map ping at a board world point (scale-1 px) — the long-press attention gesture. */
  sendPing: (at: { x: number; y: number }) => boolean;
  /** **Roll it**: release the held roll `key` on every board (`chained-resolution-sequence` 3.2). */
  sendHoldRelease: (key: string, roller: string) => boolean;
  /** The GM's **hold next** toggle (3.1). */
  sendHoldNext: (armed: boolean) => boolean;
  /** The GM's **auto-hold** toggle for this encounter (4.4). */
  sendAutoHold: (on: boolean) => boolean;
  /** The room's hold settings, or `null` before it sends them (4.4). */
  sequence: SequenceState | null;
  /** The most recent map ping the room broadcast (the transient pulse to animate), or null. */
  latestPing: PingEvent | null;
  /** Stream this client's live cursor to the room (board world point), or `null` to clear it. */
  sendCursor: (at: { x: number; y: number } | null) => boolean;
  /** Other participants' live presence cursors (board world points, labelled by slot id). */
  cursors: CursorEvent[];
  /** Stream this client's in-progress move-path preview (the dragged footprint's cells), or `null` to clear it. */
  sendMovePreview: (preview: { cells: string[] } | null) => boolean;
  /** Other participants' live drag-ghost trails (cell keys, labelled by slot id). */
  movePreviews: MovePreviewEvent[];
  /** Stream this client's Measure ruler ends, or `null` to clear it. */
  sendMeasure: (measure: { from: WireMeasureEnd; to: WireMeasureEnd } | null) => boolean;
  /** Other participants' Measure rulers (ends, labelled by slot id). */
  measures: MeasureEvent[];
  /** The route a combatant's latest chosen-path move walked (the remote walk animation reads it). */
  committedMovePath: (combatantId: string) => Cell[] | undefined;
  /**
   * The most recent command rejection the server relayed to this client (reason + category),
   * or null. Set when an optimistic action is refused (after the phantom apply is reverted),
   * cleared on the next successful change — the source for a transient categorized toast.
   *
   * Also carries the LOCAL link-down refusal (category `"link"`, reason {@link LINK_DOWN_NOTE},
   * a negative `seq` so it never collides with the server's positive ones): every write below is
   * refused while the socket is down, and this is how the refusal reaches the same toast and the
   * board's ghost snap-back (`moveRejectionSeq`). A newer server rejection supersedes it.
   */
  rejection: EncounterRejection | null;
  /**
   * Raise the link-down refusal for a write OUTSIDE this room that the link refused — a session
   * writer on the combat surface (MAKE ACTIVE / END COMBAT's `setTableScene`) — so it reaches
   * the same toast as the room's own refusals.
   */
  noteNotSent: () => void;
  /**
   * The most recent **auto-activation this client's own gesture landed**, or null — the transient
   * acknowledgement's signal (`projects/pre-activation-affordances.md` 4.1). Local to the acting
   * client by construction: only the client that made the gesture ever arms one, so a bystanding
   * player sees the turn tracker flip and nothing else. Never set by an explicit ACTIVATE TURN
   * press. See `./autoActivationNotice.ts` for the contract and the Risk behind it.
   */
  autoActivation: AutoActivationNotice | null;
  /**
   * The raw {@link RemoteEncounterController} (or null before it's constructed) — an escape
   * hatch for a caller that needs the controller itself, not this hook's flattened field-by-field
   * surface. **The one shipped consumer**: `CombatApp.tsx`
   * wraps it in a `BrowserMatchHandle` (`frontend/src/lib/encounter/browserMatchHandle.ts`) so the
   * shipped `autoplayActivation`/`autoplaySide` policy can submit over the SAME long-lived
   * connection the rest of the UI already rides — never a second connection. Every other field on
   * this hook stays the preferred surface; reach for `controller` only when a seam genuinely needs
   * the controller instance itself.
   */
  controller: RemoteEncounterController | null;
}

export type UseEncounterOptions = {
  /** Override the ws URL (tests point this at an ephemeral backend). Defaults to {@link encounterWsUrl}. */
  url?: string;
  /**
   * The `/ws/game` reclaim token folded into `encounter_join` (roster-slot-identity,
   * Decision 0016) so the server binds the socket to the verified slot. The page reads
   * it off the app-wide session; required for the default real-`WebSocket` path.
   */
  token?: string;
  /**
   * The viewer's slot is the GM's (`session.role === "gm"`). The pre-activation preview needs it:
   * the GM folds an activation on GM-owned units (`controlsActivation`, `token-command-menu` 2c).
   */
  isGm?: boolean;
  /**
   * Inject a connection + controller factory (tests drive a real backend without
   * the browser `WebSocket` global). When omitted, a real `WebSocket` is opened.
   */
  connect?: (
    encounterId: string,
    onStatus: (s: EncounterConnStatus) => void,
  ) => { controller: RemoteEncounterController; close: () => void };
}

/**
 * Open (or reuse the injected) connection to the encounter room named by
 * `encounterId` and adapt the {@link RemoteEncounterController} into React.
 *
 * `compendium` is accepted to mirror `useRemoteRoster` (and to re-connect on a
 * pack swap), though the encounter reducer reads no reference data today.
 */
export function useEncounter(
  compendium: Compendium,
  encounterId: string,
  options: UseEncounterOptions = {},
): UseEncounter {
  const { url, token, connect } = options;
  const isGm = options.isGm ?? false;

  // The encounter connection factory the generic hook drives (default real-`WebSocket`
  // via `openEncounterConnection`, the exact path the round-trip test exercises). The
  // token is folded into `encounter_join` so the server binds to the verified slot.
  const factory = useCallback(
    (onStatus: (s: ConnStatus) => void) =>
      connect
        ? connect(encounterId, onStatus)
        : openEncounterConnection(encounterId, { url: url ?? encounterWsUrl(), token: token ?? "" }, onStatus),
    [encounterId, url, token, connect],
  );

  // Retention ON: the encounter room's board must stay mounted and rendering across a
  // reconnect — `connStatus` drives the
  // "RELINKING…" pill, and `state`/`loading` must NOT blank the board on a mere drop.
  const room = useRoomController<
    EncounterSnapshot["state"],
    EncounterCommand,
    RemoteEncounterController
  >(factory, [compendium, encounterId, url, token, connect], {
    notConnectedError: "encounter room not connected",
    retainStateAcrossReconnect: true,
  });

  const controller = room.controller;
  // Idempotent: the controller's own pre-activation mirrors (`dispatchMove`'s dry run, the
  // auto-activation arm) read the viewer's role off it.
  if (controller) controller.viewerIsGm = isGm;

  // ── The write gate (connection-grace 2.1/2.3) ────────────────────────────────────────────────
  // `room.dispatch`/`undo`/`redo` already refuse while down; the intent, move, gesture and prompt
  // sends below go through the controller directly, so they are gated here on the same live read.
  // Every refusal raises the local link-down rejection. Ephemeral sends (cursor, ping, measure,
  // move preview) stay ungated: nothing durable rides them.
  const [linkRefusal, setLinkRefusal] = useState<EncounterRejection | null>(null);
  const refusalSeqRef = useRef(0);
  const refuse = (): void => {
    refusalSeqRef.current += 1;
    setLinkRefusal({ reason: LINK_DOWN_NOTE, category: "link", seq: -refusalSeqRef.current });
  };
  const serverRejection = controller?.rejection ?? null;
  useEffect(() => {
    if (serverRejection !== null) setLinkRefusal(null);
  }, [serverRejection]);
  const gated = <T,>(send: () => T, refused: T): T => {
    if (room.isLive()) return send();
    refuse();
    return refused;
  };
  const notSent = err(LINK_DOWN_NOTE);

  return {
    isGm,
    loading: room.loading,
    state: room.state,
    dispatch: (command) => {
      const result = room.dispatch(command);
      if (!result.ok && result.error === LINK_DOWN_NOTE) refuse();
      return result;
    },
    dispatchGesture: (commands) =>
      gated(() => controller?.dispatchGesture(commands) ?? err("encounter room not connected"), notSent),
    dispatchMove: (combatantId, to, opts) =>
      gated(() => controller?.dispatchMove(combatantId, to, opts) ?? err("encounter room not connected"), notSent),
    undo: () => gated(() => room.undo(), false),
    redo: () => gated(() => room.redo(), false),
    // The encounter room is single-scope: read the default scope as a value so the
    // route keeps `encounter.undoState.canUndo` (roster-scoped-undo is roster-only).
    undoState: room.undoState(),
    connStatus: room.connStatus,
    // The retry budget is spent and nothing will try again — COMBAT.app swaps its transient
    // "RELINKING…" pill for a terminal one, because with the board retained a dead room
    // otherwise looks perfectly alive. See `useRoomController`'s `linkLost` doc.
    linkLost: room.linkLost,
    reconnect: room.reconnect,
    actorId: room.actorId,
    // Resolution-intent + prompt surface, read off the live controller. The snapshot
    // republishes on every prompt/resolution change (the controller calls `publish`),
    // so `useSyncExternalStore` re-renders and these reads stay current.
    sendIntent: (intent) => gated(() => controller?.sendIntent(intent) ?? false, false),
    prompt: controller?.prompt ?? null,
    answerPrompt: (optionId) => gated(() => controller?.answerPrompt(optionId) ?? false, false),
    answerPromptById: (promptId, optionId) => gated(() => controller?.answerPromptById(promptId, optionId) ?? false, false),
    answerDecision: (promptId, optionId) =>
      gated<"sent" | "gone" | "offline">(() => controller?.answerDecision(promptId, optionId) ?? "gone", "offline"),
    hasPrompt: (promptId) => controller?.promptFor(promptId) != null,
    answerPromptDirection: (cell) => gated(() => controller?.answerPromptDirection(cell) ?? false, false),
    dismissPrompt: () => controller?.dismissPrompt(),
    pendingMove: controller?.pendingMove ?? null,
    isMoveHeld: (combatantId) => controller?.pendingMove?.combatantId === combatantId,
    tablePausedMoverId: controller?.tablePausedMoverId ?? null,
    resolutions: controller?.resolutions ?? [],
    latestResolution: controller?.latestResolution ?? null,
    sendPing: (at) => controller?.sendPing(at) ?? false,
    sendHoldRelease: (key, roller) => controller?.sendHoldRelease(key, roller) ?? false,
    sendHoldNext: (armed) => controller?.sendHoldNext(armed) ?? false,
    sendAutoHold: (on) => controller?.sendAutoHold(on) ?? false,
    sequence: controller?.sequence ?? null,
    latestPing: controller?.latestPing ?? null,
    sendCursor: (at) => controller?.sendCursor(at) ?? false,
    cursors: controller?.cursors ?? [],
    sendMovePreview: (preview) => controller?.sendMovePreview(preview) ?? false,
    movePreviews: controller?.movePreviews ?? [],
    sendMeasure: (measure) => controller?.sendMeasure(measure) ?? false,
    measures: controller?.measures ?? [],
    committedMovePath: (combatantId) => controller?.committedMovePath(combatantId),
    rejection: linkRefusal ?? serverRejection,
    noteNotSent: refuse,
    autoActivation: controller?.autoActivation ?? null,
    controller,
  };
}
